// Country + state/province options for the device-shipping address form.
// State `name` is the full label the dropshipment order payload expects
// (e.g. "Massachusetts"); country `code` is the 2-letter ISO the payload expects.

export interface GeoOption { code: string; name: string; }

export const COUNTRIES: GeoOption[] = [
  { code: 'US', name: 'United States (US)' },
  { code: 'CA', name: 'Canada (CA)' },
];

export const US_STATES: GeoOption[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

export const CA_PROVINCES: GeoOption[] = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

export function statesForCountry(country: string): GeoOption[] {
  return (country || '').toUpperCase() === 'CA' ? CA_PROVINCES : US_STATES;
}

// 2-letter code OR full name → full name (matches the Select option values);
// unknown input passes through unchanged so manual entry is never lost.
export function toStateName(input: string | null | undefined, country = 'US'): string {
  const v = (input ?? '').trim();
  if (!v) return '';
  const list = statesForCountry(country);
  const byCode = list.find((s) => s.code === v.toUpperCase());
  if (byCode) return byCode.name;
  const byName = list.find((s) => s.name.toLowerCase() === v.toLowerCase());
  return byName ? byName.name : v;
}

export interface ParsedAddress {
  address1: string;
  zip: string;
  stateName: string;
  country: string;
}

// Best-effort split of a dropshipment addressfind suggestion ({text,id}): country
// from the id prefix, trailing ZIP + 2-letter state pulled off the tail, the
// street+city remainder left in address1 for the user to confirm/split.
export function parseAddressSuggestion(s: { text?: string; id?: string }): ParsedAddress {
  const country = (String(s.id || '').split('|')[0] || 'US').toUpperCase();
  let rest = String(s.text || '').trim();
  let zip = '';
  let stateName = '';
  if (rest.includes(',')) {
    const parts = rest.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length && /\d/.test(parts[parts.length - 1] ?? '')) zip = parts.pop() ?? '';
    if (parts.length) stateName = toStateName(parts.pop(), country);
    rest = parts.join(', ');
  } else {
    const zipM = rest.match(/\s(\d{5}(?:-\d{4})?)$/);
    if (zipM) { zip = zipM[1] ?? ''; rest = rest.slice(0, zipM.index ?? rest.length).trim(); }
    const stM = rest.match(/\s([A-Za-z]{2})$/);
    if (stM) { stateName = toStateName(stM[1], country); rest = rest.slice(0, stM.index ?? rest.length).trim(); }
  }
  return {
    address1: rest, zip, stateName, country,
  };
}
